import { useState, useCallback, useRef } from 'react';
import { videoApi } from '../services/api';
import logger from '../lib/logger';

export interface UploadProgress {
  type: 'chunk_uploaded' | 'assembling' | 'uploading_to_storage' | 'extracting_metadata' | 'completed' | 'error';
  progress: number;
  chunks_received?: number;
  total_chunks?: number;
  is_final_chunk?: boolean;
  video_id?: string;
  message?: string;
}

export interface UseChunkedUploadReturn {
  upload: (file: File, onProgressUpdate?: (progress: number, status: string) => void) => Promise<string>; // Returns video_id
  cancel: () => void;
  progress: number;
  isUploading: boolean;
  uploadStatus: string;
  error: string | null;
}

export const useChunkedUpload = (): UseChunkedUploadReturn => {
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  const abortControllerRef = useRef<AbortController | null>(null);
  const websocketRef = useRef<WebSocket | null>(null);
  const activeUploadRef = useRef<string | null>(null); // Track active upload
  const lastProgressRef = useRef<number>(0); // Track last progress to prevent regression
  const uploadCompletedRef = useRef<boolean>(false); // Track if upload is completed
  
  // Performance tracking
  const uploadStartTimeRef = useRef<number>(0);
  const bytesUploadedRef = useRef<number>(0);
  
  // Calculate upload speed and ETA
  const getUploadStats = (bytesUploaded: number, totalBytes: number) => {
    const elapsedTime = (Date.now() - uploadStartTimeRef.current) / 1000; // seconds
    const uploadSpeed = bytesUploaded / elapsedTime; // bytes per second
    const remainingBytes = totalBytes - bytesUploaded;
    const eta = remainingBytes / uploadSpeed; // seconds
    
    const formatSpeed = (bytesPerSec: number) => {
      if (bytesPerSec < 1024) return `${bytesPerSec.toFixed(0)} B/s`;
      if (bytesPerSec < 1024 * 1024) return `${(bytesPerSec / 1024).toFixed(1)} KB/s`;
      return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} MB/s`;
    };
    
    const formatTime = (seconds: number) => {
      if (seconds < 60) return `${seconds.toFixed(0)}s`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
    };
    
    return {
      speed: formatSpeed(uploadSpeed),
      eta: isFinite(eta) ? formatTime(eta) : 'calculating...',
      rawSpeed: uploadSpeed
    };
  };

  // Performance configuration - can be adjusted based on connection quality
  const getOptimalConcurrency = (fileSize: number, chunkSize: number) => {
    const totalChunks = Math.ceil(fileSize / chunkSize);
    
    // Adjust concurrency based on file size and total chunks
    if (fileSize < 50 * 1024 * 1024) { // < 50MB
      return Math.min(2, totalChunks); // 2 concurrent uploads for small files
    } else if (fileSize < 200 * 1024 * 1024) { // < 200MB
      return Math.min(3, totalChunks); // 3 concurrent uploads for medium files
    } else { // >= 200MB
      return Math.min(4, totalChunks); // 4 concurrent uploads for large files
    }
  };

  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    activeUploadRef.current = null;
    lastProgressRef.current = 0;
    uploadCompletedRef.current = false;
  }, []);

  const cancel = useCallback(() => {
    // console.log('🚫 Upload cancelled by user');
    cleanup();
    setIsUploading(false);
    setProgress(0);
    setUploadStatus('Cancelled');
    setError('cancelled');
  }, [cleanup]);

  // Safe progress update function that prevents regression and adds smoothing
  const updateProgress = useCallback((newProgress: number, _source: string) => {
    const clampedProgress = Math.min(Math.max(newProgress, 0), 100);
    const lastProgress = lastProgressRef.current;
    
    // Only update if progress moves forward significantly (at least 1% or is 100%)
    const significantChange = clampedProgress >= lastProgress + 1 || clampedProgress === 100 || clampedProgress === 0;
    
    if (clampedProgress >= lastProgress && significantChange) {
      // console.log(`📊 Progress update from ${source}: ${lastProgress}% → ${clampedProgress}%`);
      setProgress(clampedProgress);
      lastProgressRef.current = clampedProgress;
    } else if (clampedProgress < lastProgress) {
      // console.log(`⚠️ Preventing progress regression from ${source}: ${clampedProgress}% < ${lastProgress}% (ignored)`);
    } else {
      // console.log(`🔄 Minor progress update from ${source}: ${clampedProgress}% (debounced, threshold not met)`);
    }
  }, []);

  const connectWebSocket = useCallback((uploadId: string, onProgressUpdate?: (progress: number, status: string) => void): Promise<void> => {
    return new Promise((resolve, _reject) => {
      // console.log('🔌 Attempting WebSocket connection for upload:', uploadId);
      
      // Clean up any existing WebSocket
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }

      // Try to get WebSocket URL from environment, fallback to backend
      const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
      
      // Determine WebSocket URL based on environment
      let wsUrl: string;
      if (baseUrl.includes('stream-clipperz.web.app') || baseUrl.includes('clipthis.io') || baseUrl === window.location.origin) {
        // If using Firebase hosting or custom domain, connect directly to Cloud Run for WebSocket
        wsUrl = `wss://streamclipper-backend-794140635987.us-central1.run.app/api/v1/upload/progress/${uploadId}`;
      } else {
        // For local development, use local WebSocket
        wsUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://') + `/api/v1/upload/progress/${uploadId}`;
      }
      
      logger.log('🔌 WebSocket URL:', wsUrl);
      const ws = new WebSocket(wsUrl);
      
      let connectionTimeout: NodeJS.Timeout;
      
      // Set a timeout for connection
      connectionTimeout = setTimeout(() => {
        logger.log('⏱️ WebSocket connection timeout, proceeding without real-time progress');
        ws.close();
        resolve(); // Don't reject, just proceed without WebSocket
      }, 5000);
      
      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        logger.log('✅ WebSocket connected successfully');
        websocketRef.current = ws;
        resolve();
      };
      
      ws.onmessage = (event) => {
        try {
          const progressData: UploadProgress = JSON.parse(event.data);
          logger.log('📨 WebSocket progress data:', progressData);
          
          let statusMessage = '';
          
          switch (progressData.type) {
            case 'chunk_uploaded':
              statusMessage = 'Uploading...';
              setUploadStatus(statusMessage);
              break;
            case 'assembling':
              statusMessage = 'Assembling file...';
              setUploadStatus(statusMessage);
              break;
            case 'uploading_to_storage':
              statusMessage = 'Uploading to cloud storage...';
              setUploadStatus(statusMessage);
              break;
            case 'extracting_metadata':
              statusMessage = 'Extracting video metadata...';
              setUploadStatus(statusMessage);
              break;
            case 'completed':
              statusMessage = 'Upload completed!';
              setUploadStatus(statusMessage);
              uploadCompletedRef.current = true; // Mark upload as completed
              // Cancel any remaining chunk uploads
              if (abortControllerRef.current) {
                abortControllerRef.current.abort();
              }
              // For completed uploads, use the exact progress (should be 100%)
              updateProgress(100, 'WebSocket-complete');
              if (onProgressUpdate) {
                onProgressUpdate(100, statusMessage);
              }
              return; // Early return to avoid double progress update
            case 'error':
              statusMessage = 'Upload failed';
              setUploadStatus(statusMessage);
              setError(progressData.message || 'Upload failed');
              logger.error('❌ WebSocket upload error:', progressData.message);
              break;
            default:
              if (progressData.message) {
                statusMessage = progressData.message;
                setUploadStatus(statusMessage);
              }
          }
          
          // Update progress for all remaining cases (completed is handled above with early return)
          updateProgress(progressData.progress, 'WebSocket');
          
          // Call the external progress callback if provided
          if (statusMessage && onProgressUpdate) {
            onProgressUpdate(progressData.progress, statusMessage);
          }
        } catch (err) {
          logger.error('❌ Failed to parse WebSocket progress data:', err);
        }
      };
      
      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        logger.warn('⚠️ WebSocket error, proceeding without real-time progress:', error);
        resolve(); // Don't reject, just proceed without WebSocket
      };
      
      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        logger.log('🔌 WebSocket closed:', event.code, event.reason);
        websocketRef.current = null;
      };
    });
  }, [updateProgress]);

  const uploadChunk = useCallback(async (
    uploadId: string,
    chunk: Blob,
    chunkNumber: number,
    isFinalChunk: boolean,
    maxRetries: number = 3
  ) => {
    // Check if upload was already completed (via WebSocket)
    if (uploadCompletedRef.current) {
      logger.log(`🚫 Skipping chunk ${chunkNumber + 1} - upload already completed`);
      return { chunk_number: chunkNumber, progress: 100, message: "Upload already completed" };
    }
    
    logger.log(`📤 Uploading chunk ${chunkNumber + 1} (${chunk.size} bytes)${isFinalChunk ? ' [FINAL]' : ''}`);
    
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Check again if upload was completed during retry attempts
        if (uploadCompletedRef.current) {
          logger.log(`🚫 Upload completed during retry - skipping chunk ${chunkNumber + 1}`);
          return { chunk_number: chunkNumber, progress: 100, message: "Upload already completed" };
        }
        
        const formData = new FormData();
        formData.append('chunk_data', chunk);

        // Use uploadApi instead of default api to get proper timeout and configuration
        const { uploadApi } = await import('../services/api');
        
        const response = await uploadApi.post(`/upload/chunk/${uploadId}`, formData, {
          params: {
            chunk_number: chunkNumber,
            is_final_chunk: isFinalChunk,
          },
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          signal: abortControllerRef.current?.signal,
          timeout: 60000, // 60 second timeout per chunk
        });

        logger.log(`✅ Chunk ${chunkNumber + 1} uploaded successfully`);
        
        return response.data;
      } catch (error: any) {
        lastError = error;
        
        // Check if error is due to upload completion (409 errors)
        if (error?.response?.status === 409) {
          logger.log(`ℹ️ Chunk ${chunkNumber + 1} upload returned 409 - upload likely completed`);
          uploadCompletedRef.current = true; // Mark as completed
          return { chunk_number: chunkNumber, progress: 100, message: "Upload already completed" };
        }
        
        if (error.message === 'cancelled' || uploadCompletedRef.current) {
          throw error;
        }
        
        logger.warn(`⚠️ Chunk ${chunkNumber + 1} upload attempt ${attempt + 1} failed:`, error.message);
        
        // Wait before retry with exponential backoff
        if (attempt < maxRetries - 1) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // Max 5 second delay
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // All retries failed
    logger.error(`❌ Chunk ${chunkNumber + 1} upload failed after ${maxRetries} attempts:`, lastError);
    throw lastError || new Error(`Chunk ${chunkNumber + 1} upload failed`);
  }, []);

  const upload = useCallback(async (file: File, onProgressUpdate?: (progress: number, status: string) => void): Promise<string> => {
    // Prevent duplicate uploads of the same file
    const fileKey = `${file.name}_${file.size}`;
    if (process.env.NODE_ENV === 'development') {
      logger.log(`🚀 Starting upload for file: ${file.name} (${file.size} bytes)`);
    }
    
    if (activeUploadRef.current === fileKey) {
      if (process.env.NODE_ENV === 'development') {
        logger.log('❌ This file is already being uploaded');
      }
      throw new Error('This file is already being uploaded');
    }
    
    if (isUploading) {
      if (process.env.NODE_ENV === 'development') {
        logger.log('❌ Another upload is already in progress');
      }
      throw new Error('Another upload is already in progress');
    }

    activeUploadRef.current = fileKey;
    setIsUploading(true);
    lastProgressRef.current = 0;
    setProgress(0);
    setUploadStatus('Starting upload...');
    setError(null);
    uploadCompletedRef.current = false; // Reset completion flag
    
    // Initialize performance tracking
    uploadStartTimeRef.current = Date.now();
    bytesUploadedRef.current = 0;
    
    // Call progress callback if provided
    onProgressUpdate?.(0, 'Starting upload...');
    
    abortControllerRef.current = new AbortController();
    
    let video_id: string = '';

    try {
      // Start upload session
      if (process.env.NODE_ENV === 'development') {
        logger.log('🏁 Initializing upload session...');
      }
      updateProgress(5, 'initialization');
      setUploadStatus('Initializing upload...');
      onProgressUpdate?.(5, 'Initializing upload...');
      
      const startResponse = await videoApi.startChunkedUpload({
        filename: file.name,
        file_size: file.size,
        content_type: file.type
      });

      const { upload_id, video_id: responseVideoId, chunk_size } = startResponse;
      video_id = responseVideoId;
      if (process.env.NODE_ENV === 'development') {
        logger.log(`📋 Upload session created: ID=${upload_id}, VideoID=${video_id}, ChunkSize=${chunk_size}`);
      }

      // Check if upload was cancelled during initialization
      if (abortControllerRef.current?.signal.aborted) {
        if (process.env.NODE_ENV === 'development') {
          logger.log('❌ Upload cancelled during initialization');
        }
        throw new Error('Upload cancelled');
      }

      // Connect to WebSocket for progress updates
      await connectWebSocket(upload_id, onProgressUpdate);

      // Calculate chunks
      const totalChunks = Math.ceil(file.size / chunk_size);
      if (process.env.NODE_ENV === 'development') {
        logger.log(`📦 File will be split into ${totalChunks} chunks`);
      }
      
      updateProgress(10, 'chunk-upload-start');
      setUploadStatus(`Uploading...`);
      onProgressUpdate?.(10, `Uploading...`);

      // Upload chunks in parallel for better performance
      const maxConcurrentUploads = getOptimalConcurrency(file.size, chunk_size);
      const _chunkPromises: Promise<void>[] = [];
      let completedChunks = 0;
      
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += maxConcurrentUploads) {
        // Create batch of chunks to upload in parallel
        const batchPromises: Promise<void>[] = [];
        
        for (let i = 0; i < maxConcurrentUploads && (chunkIndex + i) < totalChunks; i++) {
          const currentChunkIndex = chunkIndex + i;
          const start = currentChunkIndex * chunk_size;
          const end = Math.min(start + chunk_size, file.size);
          const chunk = file.slice(start, end);
          const isFinalChunk = currentChunkIndex === totalChunks - 1;

          const chunkPromise = uploadChunk(upload_id, chunk, currentChunkIndex, isFinalChunk)
          // eslint-disable-next-line no-loop-func
            .then(() => {
              // Check if upload was completed during this chunk upload
              if (uploadCompletedRef.current) {
                logger.log(`🏁 Upload completed during chunk processing - terminating remaining chunks`);
                return;
              }
              
              // Use functional approach to avoid loop variable capture
              // eslint-disable-next-line no-loop-func
              const newCompletedChunks = ++completedChunks;
              bytesUploadedRef.current += chunk.size;
              
              // Calculate local progress (only use if WebSocket isn't providing updates)
              const localProgress = 10 + (newCompletedChunks / totalChunks) * 75; // 10% -> 85%
              
              // Get performance stats
              const stats = getUploadStats(bytesUploadedRef.current, file.size);
              const statusMessage = `Uploading... (${newCompletedChunks}/${totalChunks} chunks) - ${stats.speed} - ETA: ${stats.eta}`;
              
              // Only update with local progress if it's higher than current
              if (localProgress > lastProgressRef.current && !uploadCompletedRef.current) {
                updateProgress(localProgress, 'local-chunk-progress');
                setUploadStatus(statusMessage);
                onProgressUpdate?.(localProgress, statusMessage);
              }
            });
          
          batchPromises.push(chunkPromise);
        }
        
        // Wait for current batch to complete before starting next batch
        await Promise.all(batchPromises);
        
        // Check for cancellation between batches
        if (abortControllerRef.current?.signal.aborted) {
          throw new Error('Upload cancelled');
        }
      }

      // Wait for final processing (WebSocket should update progress to 100%)
      // Ensure we're at least at 90% before finalizing
      const finalProgress = Math.max(lastProgressRef.current, 90);
      // console.log(`🏁 Upload chunks complete, finalizing at ${finalProgress}%`);
      
      // If upload was completed via WebSocket, don't update progress
      if (!uploadCompletedRef.current) {
        updateProgress(finalProgress, 'finalization');
        setUploadStatus('Finalizing upload...');
        onProgressUpdate?.(finalProgress, 'Finalizing upload...');
      }

      // console.log(`✅ Upload completed successfully! VideoID: ${video_id}`);
      return video_id;

    } catch (err: any) {
      const errorMessage = err.message || 'Upload failed';
      logger.error('❌ Upload failed:', errorMessage);
      
      // Don't show error if upload was actually completed via WebSocket
      if (errorMessage !== 'cancelled' && !uploadCompletedRef.current) {
        setError(errorMessage);
        setUploadStatus('Upload failed');
        onProgressUpdate?.(lastProgressRef.current, 'Upload failed');
      }
      
      // If upload was completed, return success instead of throwing
      if (uploadCompletedRef.current) {
        logger.log('✅ Upload completed via WebSocket despite chunk error');
        return video_id;
      }
      
      throw err;
    } finally {
      setIsUploading(false);
      cleanup();
    }
  }, [connectWebSocket, uploadChunk, cleanup, updateProgress, isUploading]);

  return {
    upload,
    cancel,
    progress,
    isUploading,
    uploadStatus,
    error
  };
}; 